import { usePageTitle } from "../hooks/usePageTitle";
import samanthaPhoto from "../assets/samantha-photo.jpg";

export default function About() {
  usePageTitle("About");
  return (
    <>
      <section style={{ padding: "3.5rem 20px 2rem", textAlign: "center", maxWidth: 680, margin: "0 auto" }}>
        <p style={{ fontSize: 13, color: "var(--lilac-deep)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 8px" }}>
          About Groundwork TierUp
        </p>
        <h1
          style={{
            fontFamily: "var(--font-voice)",
            fontSize: "clamp(26px, 4vw, 36px)",
            color: "var(--navy)",
            margin: "0 0 16px",
            lineHeight: 1.25,
          }}
        >
          Twenty Years of Reality, Built In
        </h1>
        <p style={{ fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.7, margin: "0 0 16px" }}>
          Groundwork helps delivery leaders connect project management, change management, and business improvement in one place.
        </p>
        <p style={{ fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.7, margin: "0 0 16px" }}>
          Most delivery frameworks assume the hard part is creating the plan. It isn&rsquo;t.
        </p>
        <p style={{ fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.7, margin: "0 0 16px" }}>
          The hard part is what happens afterwards: the sponsor who disappears halfway through, the decision that gets reopened for the fourth time, the stakeholder who thought someone else was responsible, the project that&rsquo;s six weeks behind before anyone admits it, the change that looked simple until people had to live with it.
        </p>
        <p style={{ fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.7, margin: "0 0 16px" }}>
          Groundwork TierUp was built from two decades of project delivery, change management, and business improvement across government and industry. Not as another methodology. Not as another task tracker. As the practical system I wished existed when I was walking into difficult projects, inheriting work already in trouble, and being expected to have answers by tomorrow morning.
        </p>
        <p style={{ fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.7 }}>
          Every workflow, guide, template, and tool in Groundwork started as something that solved a real problem before it became software.
        </p>
      </section>

      <section style={{ padding: "0 20px", margin: "0 0 3rem", textAlign: "center" }}>
        <p style={{ fontFamily: "var(--font-voice)", fontSize: 19, color: "var(--navy)", maxWidth: 620, margin: "0 auto", lineHeight: 1.5, fontStyle: "italic" }}>
          &ldquo;Groundwork is the tool I built after twenty years of being expected to have an answer by tomorrow morning.&rdquo;
        </p>
      </section>

      <section
        style={{
          display: "flex",
          gap: 40,
          alignItems: "center",
          flexWrap: "wrap",
          padding: "2rem",
          maxWidth: 880,
          margin: "0 auto 2rem",
          background: "var(--surface-1)",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--border)",
        }}
      >
        <img
          src={samanthaPhoto}
          alt="Samantha, founder of Groundwork TierUp"
          style={{
            width: 220,
            height: 220,
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
            border: "3px solid var(--surface-0)",
            boxShadow: "var(--shadow-md)",
          }}
        />
        <div style={{ flex: "1 1 320px", minWidth: 280 }}>
          <p style={{ fontFamily: "var(--font-voice)", fontSize: 28, fontWeight: 600, color: "var(--navy)", margin: "0 0 14px" }}>
            Hi, I&rsquo;m Samantha
          </p>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: "0 0 14px", lineHeight: 1.7 }}>
            I&rsquo;ve spent two decades in project, change, and business improvement work across government and private sectors, long enough to watch a lot of good frameworks die in the gap between the workshop and the Tuesday standup.
          </p>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: "0 0 14px", lineHeight: 1.7 }}>
            The real limit was never the methodology. It was administrative drag: duplicated data entry, clunky tools, and disconnected templates that refuse to talk to each other. So I built the tool I kept wishing existed, focused on quick actions for adding and updating information once, and generating clean, repeatable outputs you can send in a flash.
          </p>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: 0, lineHeight: 1.7 }}>
            Not another theoretical framework to learn. Groundwork TierUp is the delivery workbook built for people doing this work for real.
          </p>
        </div>
      </section>

      <section style={{ background: "var(--lilac-wash)", padding: "3rem 2rem", margin: "32px 0", textAlign: "center" }}>
        <p style={{ fontFamily: "var(--font-voice)", fontSize: 20, color: "var(--navy)", margin: 0, lineHeight: 1.4, maxWidth: 580, marginLeft: "auto", marginRight: "auto" }}>
          You don&rsquo;t need another framework.
          <br />
          You need the one you already trust, ready when the room needs it.
        </p>
      </section>

      <section style={{ padding: "0 20px 40px", maxWidth: 640, margin: "0 auto" }}>
        <h2 style={{ fontFamily: "var(--font-voice)", fontSize: 22, color: "var(--navy)", margin: "0 0 14px" }}>
          Why this exists
        </h2>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.7, margin: 0 }}>
          Most PM tools assume the plan is the hard part. In practice, the plan is usually fine. It&rsquo;s re-explaining the same judgment call for the fifth time that costs the day. Groundwork TierUp exists to hold that judgment so it doesn&rsquo;t have to be relitigated every time.
        </p>
      </section>
    </>
  );
}
